import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { TeiasoftModalPayload } from '../../models/app/teiasoft-modal.model';

@Injectable({
  providedIn: 'root'
})
export class TeiasoftModalService {
  private teiasoftModalStartSource = new Subject<TeiasoftModalPayload>();

  /**
   * Observable stream consumed by one or more <teiasoft-modal> host components.
   *
   * Important:
   * this service does not render any UI by itself. Calling show()/showDiscard()
   * only publishes a payload on this stream.
   *
   * For a modal to appear, at least one <teiasoft-modal> must be mounted in the
   * current component tree (locally or in an ancestor) and listening on the
   * matching scope.
   *
   * If no host is mounted, no modal is shown.
   * If multiple hosts are mounted on the same scope, more than one host can
   * react to the same payload.
   */
  teiasoftModalStart$ = this.teiasoftModalStartSource.asObservable();

  constructor() { }

  /**
   * Show a generic modal.
   *
   * The modal is rendered by the first matching <teiasoft-modal> host(s) found
   * in the active UI tree for the provided scope.
   */
  show(
    type: string,
    title: string,
    message: string,
    parameter: any,
    closeFlag: string,
    showNotesInput = false,
    notesLabel?: string,
    cancelFlag?: string,
    scope: string = 'global'
  ): void {
    const payload: TeiasoftModalPayload = {
      type,
      title,
      message,
      scope,
      parameter,
      flags: { close: closeFlag, cancel: cancelFlag },
      showNotesInput,
      notesLabel
    };
    this.teiasoftModalStartSource.next(payload);
  }

  /**
   * Show a discard modal (apply / discard / cancel).
   *
   * As for show(), this method only dispatches the payload. Rendering depends
   * on a mounted <teiasoft-modal> host on the same scope.
   */
  showDiscard(
    title: string,
    message: string,
    parameter: any,
    applyFlag: string = '',
    discardFlag: string = '',
    cancelFlag: string = '',
    scope: string = 'global'
  ): void {
    const payload: TeiasoftModalPayload = {
      type: 'discard',
      title,
      message,
      scope,
      parameter,
      flags: {
        apply: applyFlag,
        discard: discardFlag,
        cancel: cancelFlag
      }
    };
    this.teiasoftModalStartSource.next(payload);
  }
}
